import React, { useState, useEffect, useCallback } from 'react';
import { DashboardService } from './dashboardService';
import { CompanyService } from '../company/companyService';
import { LoggedUserService } from '../loggedUser/loggedUserService';
import { RoleConstant } from '../../app/roleConstant';
import { DateTimeService } from '../utils/dateTimeService';
import { EquipmentService } from '../equipment/equipmentService';
import { PercentageData } from './percentageData';
import { HistogramService } from './histogramService';
import { FilterDatePicker } from '../components/filterDatePicker';
import { HistogramChart } from './histogramChart';
import { EmployeeChart } from './employeeChart';
import { CategoryChart } from './categoryChart';
import { PercentageChart } from './percentageChart';
import { InspectionTypeChart } from './inspectionTypeChart';
import { ConformityTrendChart } from './conformityTrendChart';
import { TopProductsChart } from './topProductsChart';
import moment from 'moment';
import { dashboardStyles as styles } from './dashboardStyles';

export const DashboardPage = ({ currentLanguage }) => {
    const [state, setState] = useState({
        percentageData: null,
        histogramData: null,
        employeeData: null,
        categoryData: null,
        inspectionTypeData: null,
        conformityTrendData: null,
        topProductsData: null,
        users: DashboardService.buildEmptyStats(),
        equipments: DashboardService.buildEmptyStats(),
        inspections: DashboardService.buildEmptyStats(),
        loggedUser: {},
        dateSearchFrom: null,
        dateSearchUpTo: null,
        companies: [],
        filterCompanyId: '',
    });

    // Load general data (KPIs + General Charts) - NOT FILTERED BY COMPANY
    const loadGeneralData = useCallback(() => {
        // ALWAYS pass null or empty string to ignore company filter if the API supports it,
        // or ensure the API call here doesn't use the filter.
        // The user specifically asked for "dados que n são alterados pelo filtro".
        // So we call services without companyId.

        DashboardService.summary('').then(summary => setState(prev => ({
            ...prev,
            users: {
                allTimeTotal: summary.users.allTimeTotal,
                last7DaysTotal: summary.users.last7DaysTotal,
                lastCreateTime: DateTimeService.formatDateTime(summary.users.lastCreateTime)
            },
            equipments: {
                allTimeTotal: summary.equipments.allTimeTotal,
                last7DaysTotal: summary.equipments.last7DaysTotal,
                lastCreateTime: DateTimeService.formatDateTime(summary.equipments.lastCreateTime)
            },
            inspections: {
                allTimeTotal: summary.inspections.allTimeTotal,
                last7DaysTotal: summary.inspections.last7DaysTotal,
                lastCreateTime: DateTimeService.formatDateTime(summary.inspections.lastCreateTime)
            },
        })));

        DashboardService.inspectionTypeInspections('').then(inspectionTypeInspections => {
            setState(prev => ({
                ...prev,
                inspectionTypeData: inspectionTypeInspections
            }));
        });
    }, []);

    // Load filtered chart data
    const loadFilteredData = useCallback((companyId, dateFrom, dateTo) => {
        const dateStart = dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : '2018-01-01';
        const dateEnd = dateTo ? moment(dateTo).format('YYYY-MM-DD') : moment(Date.now()).format('YYYY-MM-DD');

        DashboardService.equipmentStatus(companyId).then(equipmentStatus => {
            setState(prev => ({
                ...prev,
                percentageData: PercentageData.of(
                    equipmentStatus,
                    ['EXPIRED', 'IMPROPER', 'VALID'],
                    EquipmentService.calculateStatusLabel,
                    EquipmentService.calculateStatusColor,
                    currentLanguage
                )
            }));
        });

        DashboardService.monthlyInspections(companyId).then(monthlyInspections => {
            setState(prev => ({
                ...prev,
                histogramData: HistogramService.convertData(monthlyInspections)
            }));
        });

        DashboardService.employeeInspections(dateStart, dateEnd, companyId).then(employeeInspections => {
            setState(prev => ({
                ...prev,
                employeeData: employeeInspections
            }));
        });

        DashboardService.productInspections(dateStart, dateEnd, companyId).then(productInspections => {
            setState(prev => ({
                ...prev,
                categoryData: productInspections,
                conformityTrendData: DashboardService.processConformityTrend(productInspections),
                topProductsData: DashboardService.processTopProducts(productInspections, 5)
            }));
        });
    }, [currentLanguage]);

    useEffect(() => {
        CompanyService.list().then(companies => setState(prev => ({ ...prev, companies })));
        LoggedUserService.getLoggedUser().then(loggedUser => {
            setState(prev => ({ ...prev, loggedUser }));
        });

        // Load general data ONCE (unfiltered)
        loadGeneralData();

        // Load initial filtered data
        loadFilteredData('', null, null);
    }, [loadGeneralData, loadFilteredData]);

    const handleFilterChange = (newCompanyId) => {
        setState(prev => ({ ...prev, filterCompanyId: newCompanyId }));
        // Only reload filtered data, NOT general data
        loadFilteredData(newCompanyId, state.dateSearchFrom, state.dateSearchUpTo);
    };

    const handleSearchButton = () => {
        const { filterCompanyId, dateSearchFrom, dateSearchUpTo } = state;
        loadFilteredData(filterCompanyId, dateSearchFrom, dateSearchUpTo);
    };

    const {
        percentageData,
        histogramData,
        employeeData,
        categoryData,
        inspectionTypeData,
        conformityTrendData,
        topProductsData,
        users,
        equipments,
        inspections,
        loggedUser,
        dateSearchFrom,
        dateSearchUpTo,
        companies,
        filterCompanyId
    } = state;

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                {/* Filters Row - Moved to Top */}
                {loggedUser.currentRole !== RoleConstant.COMPANY_OPERATOR && (
                    <div style={{ ...styles.filtersRow, marginBottom: '32px' }}>
                        <div style={styles.filterGroup}>
                            <span style={styles.filterLabel}>{currentLanguage.company}:</span>
                            <select
                                style={styles.select}
                                value={filterCompanyId || ''}
                                onChange={event => handleFilterChange(event.target.value)}
                            >
                                <option value="">{currentLanguage.all.toUpperCase()}</option>
                                {companies.map(company => (
                                    <option key={company.id} value={company.id}>{company.name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <span style={styles.filterLabel}>{currentLanguage.from}:</span>
                            <FilterDatePicker
                                disableFuture
                                onChange={date => setState(prev => ({ ...prev, dateSearchFrom: date }))}
                                value={dateSearchFrom}
                            />
                        </div>
                        <div style={styles.filterGroup}>
                            <span style={styles.filterLabel}>{currentLanguage.to}:</span>
                            <FilterDatePicker
                                disableFuture
                                onChange={date => setState(prev => ({ ...prev, dateSearchUpTo: date }))}
                                value={dateSearchUpTo}
                            />
                        </div>
                        <button
                            style={{
                                ...styles.filterBtn,
                                opacity: (dateSearchFrom === null || dateSearchUpTo === null) ? 0.6 : 1,
                                cursor: (dateSearchFrom === null || dateSearchUpTo === null) ? 'not-allowed' : 'pointer'
                            }}
                            onClick={handleSearchButton}
                            disabled={dateSearchFrom === null || dateSearchUpTo === null}
                        >
                            🔍 {currentLanguage.filter || 'Filtrar'}
                        </button>
                    </div>
                )}

                {/* General Data Section (Unfiltered) */}
                <div style={styles.sectionTitle}>
                    <span>🌐</span> {currentLanguage.general_overview || 'Visão Geral (Geral)'}
                </div>

                {/* Custom Grid for General Data: 3 KPIs + 1 Chart */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                }}>
                    <div style={styles.kpiCard}>
                        <div style={{ ...styles.kpiIcon, backgroundColor: '#3B82F6' }}>👥</div>
                        <div>
                            <div style={styles.kpiValue}>{users.allTimeTotal}</div>
                            <div style={styles.kpiLabel}>{currentLanguage.users}</div>
                            <div style={styles.kpiMeta}>
                                {users.last7DaysTotal} {currentLanguage.new_in_last_7_days || 'novos nos últimos 7 dias'}
                            </div>
                            <div style={styles.kpiSubtext}>
                                {currentLanguage.last_interaction || 'Última'}: {users.lastCreateTime}
                            </div>
                        </div>
                    </div>
                    <div style={styles.kpiCard}>
                        <div style={{ ...styles.kpiIcon, backgroundColor: '#8B5CF6' }}>🚜</div>
                        <div>
                            <div style={styles.kpiValue}>{equipments.allTimeTotal}</div>
                            <div style={styles.kpiLabel}>{currentLanguage.equipments}</div>
                            <div style={styles.kpiMeta}>
                                {equipments.last7DaysTotal} {currentLanguage.new_in_last_7_days || 'novos nos últimos 7 dias'}
                            </div>
                            <div style={styles.kpiSubtext}>
                                {currentLanguage.last_interaction || 'Última'}: {equipments.lastCreateTime}
                            </div>
                        </div>
                    </div>
                    <div style={styles.kpiCard}>
                        <div style={{ ...styles.kpiIcon, backgroundColor: '#22C55E' }}>📋</div>
                        <div>
                            <div style={styles.kpiValue}>{inspections.allTimeTotal}</div>
                            <div style={styles.kpiLabel}>{currentLanguage.inspections}</div>
                            <div style={styles.kpiMeta}>
                                {inspections.last7DaysTotal} {currentLanguage.new_in_last_7_days || 'novos nos últimos 7 dias'}
                            </div>
                            <div style={styles.kpiSubtext}>
                                {currentLanguage.last_interaction || 'Última'}: {inspections.lastCreateTime}
                            </div>
                        </div>
                    </div>

                    {/* Inspection Type Chart moved here */}
                    <div style={styles.chartCard}>
                        <div style={{ ...styles.chartTitle, fontSize: '0.9rem', marginBottom: '10px' }}>
                            <span>🔍</span> {currentLanguage.inspection_type_distribution || 'Tipos de Inspeção'}
                        </div>
                        <div style={{ height: '160px', position: 'relative', width: '100%' }}>
                            <InspectionTypeChart data={inspectionTypeData} language={currentLanguage} />
                        </div>
                    </div>
                </div>

                <hr style={{ border: '0', borderTop: '1px solid #E5E7EB', margin: '32px 0' }} />

                {/* Primary Charts (Filtered) */}
                <div style={styles.sectionTitle}>
                    <span>📊</span> {currentLanguage.detailed_analysis || 'Análise (Filtrada)'}
                </div>
                <div style={styles.chartsRow}>
                    <div style={styles.chartCard}>
                        <div style={styles.chartTitle}>
                            <span>📈</span> {currentLanguage.inspections_by_month}
                        </div>
                        <div style={{ height: '300px', position: 'relative', width: '100%' }}>
                            <HistogramChart data={histogramData} />
                        </div>
                    </div>
                    <div style={styles.chartCard}>
                        <div style={styles.chartTitle}>
                            <span>⚠️</span> {currentLanguage.equipments_status}
                        </div>
                        <div style={{ position: 'relative', height: '220px', width: '100%' }}>
                            <PercentageChart data={percentageData} />
                        </div>
                        {percentageData && percentageData.xData && (
                            <div style={{ marginTop: '16px' }}>
                                {percentageData.xData.map((item, i) => (
                                    <div key={i} style={styles.legendItem}>
                                        <div style={{ ...styles.legendDot, backgroundColor: item.color || '#E5E7EB' }}></div>
                                        <span>{item.value} {item.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.detailsRow}>
                    <div style={styles.chartCard}>
                        <div style={styles.chartTitle}>
                            <span>🏆</span> {currentLanguage.inspections_by_employees}
                        </div>
                        <div style={{ minHeight: '350px', position: 'relative', width: '100%' }}>
                            <EmployeeChart data={employeeData} />
                        </div>
                    </div>
                    <div style={styles.chartCard}>
                        <div style={styles.chartTitle}>
                            <span>📦</span> {currentLanguage.inspections_by_products}
                        </div>
                        <div style={{ minHeight: '350px', position: 'relative', width: '100%' }}>
                            <CategoryChart data={categoryData} language={currentLanguage} />
                        </div>
                    </div>
                </div>

                <div style={styles.additionalChartsRow}>
                    <div style={styles.chartCard}>
                        <div style={styles.chartTitle}>
                            <span>📊</span> {currentLanguage.conformity_trend || 'Tendência de Conformidade'}
                        </div>
                        <div style={{ height: '300px', position: 'relative', width: '100%' }}>
                            <ConformityTrendChart data={conformityTrendData} language={currentLanguage} />
                        </div>
                    </div>
                    <div style={styles.chartCard}>
                        <div style={styles.chartTitle}>
                            <span>🏅</span> {currentLanguage.top_products || 'Top 5 Produtos Mais Inspecionados'}
                        </div>
                        <div style={{ height: '300px', position: 'relative', width: '100%' }}>
                            <TopProductsChart data={topProductsData} language={currentLanguage} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};